import Pusher from "pusher-js";
import { debugError, debugLog } from "../utils";
import { rrFetch } from "../utils/rr_fetch"

Pusher.logToConsole = process.env.PARCEL_DEBUG_MODE === "true";

class ChannelServiceInternal {
  constructor() {
    this.types = {}; // { [type]: { name, channel, status, pendingName, debounceId } }
    this.pusher = new Pusher(process.env.PARCEL_PUSHER_KEY, {
      cluster: process.env.PARCEL_PUSHER_CLUSTER || "us2",
    });

    this.pusher.connection.bind("state_change", s => debugLog("pusher state:", s));
    this.pusher.connection.bind("error", e => console.warn("pusher error:", e));

    this.connect(); // meta tag bootstrap
    window.addEventListener("beforeunload", () => this.disconnect());
  }

  connect(channel = null) {
    const ch =
      (typeof channel === "string" && channel.trim()) ||
      this._readChannelFromDOM();
    if (!ch) return;
    this.subscribeToChannel("stream", ch);
  }

  disconnect() {
    Object.keys(this.types).forEach(type => this._forceUnsubscribe(type));
    this.pusher.disconnect();
  }

  _readChannelFromDOM() {
    const el = document.querySelector('meta[name="pusher-channel"]');
    return el?.content?.trim() || null;
  }

  subscribeToChannel(type, identifier) {
    const channelName = (identifier || "").trim();
    if (!channelName) return;

    // simple debounce in case Stimulus connect fires multiple times rapidly
    clearTimeout(this.types[type]?.debounceId);
    this.types[type] = this.types[type] || { status: "idle" };
    this.types[type].debounceId = setTimeout(() => this._subscribeCore(type, channelName), 0);
  }

  _subscribeCore(type, channelName) {
    const state = this.types[type] || { status: "idle" };

    // If already subscribed/subscribing to exactly this name → no-op
    if ((state.status === "subscribed" || state.status === "subscribing") && state.name === channelName) {
      debugLog("ChannelService: already on channel", { type, channelName, status: state.status });
      return;
    }

    // If a subscribe is in-flight to a different name, remember the desired one and switch after
    if (state.status === "subscribing") {
      state.pendingName = channelName;
      debugLog("ChannelService: queue switch after subscribe", { type, from: state.name, to: channelName });
      return;
    }

    // If subscribed to a different channel, cleanly unsubscribe first
    if (state.status === "subscribed" && state.name && state.name !== channelName) {
      this._safeUnsubscribe(type);
    }

    // Begin subscribe
    debugLog("ChannelService: subscribing", { type, channelName });
    const channel = this.pusher.subscribe(channelName);

    // Update local state *before* events arrive
    this.types[type] = { name: channelName, channel, status: "subscribing", pendingName: null };

    // Pusher lifecycle binds
    channel.bind("pusher:subscription_succeeded", () => {
      const curr = this.types[type];
      if (!curr || curr.name !== channelName) return; // stale
      curr.status = "subscribed";
      debugLog("ChannelService: subscription_succeeded", { type, channelName });

      // If a newer request arrived while subscribing, switch now
      if (curr.pendingName && curr.pendingName !== curr.name) {
        const next = curr.pendingName;
        curr.pendingName = null;
        this._safeUnsubscribe(type, () => this._subscribeCore(type, next));
      }
    });

    channel.bind("pusher:subscription_error", (err) => {
      const curr = this.types[type];
      if (curr && curr.name === channelName) curr.status = "idle";
      console.warn("ChannelService: subscription_error", { type, channelName, err });
    });

    // --- App events ---

    // Direct Turbo Stream HTML payloads
    channel.bind("html", (data) => {
      Turbo.renderStreamMessage(data);
    });

    // Simple redirect payloads
    channel.bind("redirect", (data) => {
      if (data?.url) window.location.href = data.url;
    });

    channel.bind("onramp_received", () => {
      this.fetchBottomsheet("/meld/deposit-initiated");
    });

    channel.bind("waas_funded", () => {
      this.fetchBottomsheet("/meld/deposit-confirmed");
    });

    channel.bind("onramp_failed", () => {
      this.fetchBottomsheet("/meld/deposit-failed");
    });

    // Text messages for simple refresh signals
    channel.bind("text", (data) => {
      const message =
        typeof data === "string"
          ? data
          : (data?.message || data?.type || "");

      if (message === "activity_updated") {
        this.refreshActivityAndBalance();
      } else if (message === "refresh_activity") {
        debugLog("Refreshing activity page");
        window.location.reload();
      } else if (data?.type == "deposit_status_updated") {
        const swapId = data.swapId
        this.refreshPendingDepositStatus(swapId)
      }
    });
  }

  // The activity screen keeps its filters in the URL; refreshes pass them on
  // so new activity does not clear what the user filtered by.
  activityFilterQuery() {
    if (!window.location.pathname.endsWith("/activity")) return "";
    const type = new URLSearchParams(window.location.search).get("type");
    return type ? `?type=${encodeURIComponent(type)}` : "";
  }

  refreshActivity() {
    debugLog("Refreshing activity page content");

    // Make a GET request to /activity to refresh the content
    rrFetch(`/activity${this.activityFilterQuery()}`, {
      method: 'GET',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'text/html',
      },
      credentials: 'same-origin'
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.text();
    })
    .then(html => {
      // Replace the content of the activity frame
      const activityFrame = document.querySelector('[data-controller*="transaction-history"]');
      if (activityFrame) {
        // Parse the returned HTML and extract just the activity content
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newActivityContent = doc.querySelector('[data-controller*="transaction-history"]');

        if (newActivityContent) {
          activityFrame.innerHTML = newActivityContent.innerHTML;
        } else {
          debugLog("Could not find activity content in returned HTML");
        }
      } else {
        debugLog("Activity frame not found - user not on activity page, skipping refresh");
      }
    })
    .catch(error => {
      debugError("Failed to refresh activity page:", error);
      // Fallback to full page reload
      window.location.reload();
    });
  }

  refreshActivityAndBalance() {
    return rrFetch(`/activity/sync-activity-and-balance${this.activityFilterQuery()}`, {
      method: 'GET',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'text/vnd.turbo-stream.html',
      },
      credentials: 'same-origin'
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.text();
    })
    .then(turboStream => {
      Turbo.renderStreamMessage(turboStream);
    })
    .catch(error => {
      debugError("Failed to sync activity and balance:", error);
      // fallback keeps previous behavior if combined stream fails
      this.refreshActivity();
      this.syncHomeBalance();
    });
  }

  refreshHome() {
    const homeFrame = document.querySelector('.wallet-home');
    if (!homeFrame) {
      debugLog("Home frame not found - user not on home page, skipping refresh");
      return Promise.resolve();
    }

    // Make a GET request to /home to refresh the content
    return rrFetch('/home', {
      method: 'GET',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'text/html',
      },
      credentials: 'same-origin'
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.text();
    })
    .then(html => {
      // Replace the content of the home frame - look for the div with wallet-home class
      // Parse the returned HTML and extract just the .wallet-home div's content
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newHomeContent = doc.querySelector('.wallet-home');
      if (newHomeContent) {
        homeFrame.innerHTML = newHomeContent.innerHTML;
      } else {
        debugLog("Could not find .wallet-home in returned HTML");
      }
    })
    .catch(error => {
      debugError("Failed to refresh home page:", error);
      // Fallback to full page reload
      window.location.reload();
    });
  }

  syncHomeBalance() {
    const balanceEl = document.getElementById("home-balance-amount");
    if (!balanceEl) {
      return Promise.resolve();
    }

    return rrFetch('/home/balance', {
      method: 'GET',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'text/vnd.turbo-stream.html',
      },
      credentials: 'same-origin'
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.text();
    })
    .then(turboStream => {
      Turbo.renderStreamMessage(turboStream);
    })
    .catch(error => {
      console.error("Failed to sync home balance:", error);
    });
  }

  refreshPendingDepositStatus(swapId) {
    debugLog("Refreshing deposit status", { swapId });

    // Check if the target element exists before fetching
    const statusContainer = document.getElementById('deposit-status-container');
    if (!statusContainer) {
      debugLog("Status container not found - user not on deposit page, skipping update");
      return;
    }

    const containerSwapId = String(statusContainer.dataset.swapId || "").trim();
    const incomingSwapId = String(swapId ?? "").trim();
    if (containerSwapId && incomingSwapId && containerSwapId !== incomingSwapId) {
      debugLog("Deposit status event for a different swap - skipping", {
        containerSwapId,
        incomingSwapId,
      });
      return;
    }

    // Make a GET request to get the updated status turbo-stream
    rrFetch(`/deposit/crypto/order-status/${swapId}`, {
      method: 'GET',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'text/vnd.turbo-stream.html',
      },
      credentials: 'same-origin'
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.text();
    })
    .then(turboStream => {
      const current = document.getElementById('deposit-status-container');
      if (!current) {
        debugLog("Status container removed during fetch - skipping render");
        return;
      }
      const currentSwapId = String(current.dataset.swapId || "").trim();
      if (currentSwapId && incomingSwapId && currentSwapId !== incomingSwapId) {
        debugLog("Status container changed swap during fetch - skipping render", {
          currentSwapId,
          incomingSwapId,
        });
        return;
      }
      debugLog("Rendering deposit status turbo-stream", { swapId });
      Turbo.renderStreamMessage(turboStream);
    })
    .catch(error => {
      console.error("Failed to refresh deposit status:", error);
    });
  }

  fetchBottomsheet(url) {
    return rrFetch(url, {
      method: 'GET',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'text/vnd.turbo-stream.html',
      },
      credentials: 'same-origin'
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.text();
    })
    .then(turboStream => {
      Turbo.renderStreamMessage(turboStream);
    })
    .catch(error => {
      debugError("Failed to fetch bottomsheet:", error);
    });
  }

  _safeUnsubscribe(type, then) {
    const state = this.types[type];
    if (!state?.channel || state.status === "unsubscribing") {
      if (then) then();
      return;
    }
    state.status = "unsubscribing";
    debugLog("ChannelService: unsubscribing", { type, channelName: state.name });

    // Pusher has no explicit callback for client-initiated unsubscribe success; do local cleanup immediately.
    state.channel.unbind_all();
    this.pusher.unsubscribe(state.name);
    // Reset state
    this.types[type] = { status: "idle" };
    if (then) then();
  }

  _forceUnsubscribe(type) {
    const state = this.types[type];
    if (!state?.channel) return;
    try {
      state.channel.unbind_all();
      this.pusher.unsubscribe(state.name);
    } finally {
      this.types[type] = { status: "idle" };
    }
  }
}

// Hard singleton export
if (!window.__ChannelService) window.__ChannelService = new ChannelServiceInternal();
export default window.__ChannelService;
